import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { applyAdminPrintSettings, isAdminManaged } from '../../utils/adminPrintSettings';
import { renderExactPreview } from '../../utils/printPreviewResponder';

// Default printer settings
const DEFAULT_SETTINGS = {
  printEnabled: 'true', // Master toggle — disable to stop all printing (auto-accept + manual)
  paperWidth: '58', // 58mm or 80mm
  paperHeight: '150', // mm (6 inches = ~152mm)
  fontSize: '9', // px
  fontWeight: 'bold',
  lineHeight: '1.15',
  marginLeft: '2', // px
  marginRight: '2', // px
  marginBottom: '0', // mm — extra space after content before cut
  printerType: 'escpos', // 'escpos' (Epson/Star/Citizen/most receipt printers) or 'tspl' (TSC label printers)
  printDirection: '1', // '0' = normal, '1' = 180° rotated (for printers that feed from bottom)
  printDensity: '8', // 0-15, higher = darker print
  printSpeed: '4', // 1-6, higher = faster
  rasterBandHeight: '0', // ESC/POS only: 0=off (single raster, default). >0 splits the image into N-dot-row bands — fixes printers (e.g. OCOM) that print ~80% then spill the rest onto the next receipt
  kotEnabled: 'true', // Print KOT (Kitchen Order Ticket) along with receipt
  kotOnly: 'false', // When 'true': print ONLY the KOT — skip the customer receipt entirely (wins over kotEnabled)
};

function PrinterSettings() {
  const { restaurant } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [systemPrinters, setSystemPrinters] = useState([]);
  const adminManaged = isAdminManaged(restaurant);

  // Accurate print preview (the exact raster this terminal would print)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState('');
  const [previewKind, setPreviewKind] = useState('receipt');

  const supportsExactPreview = !!window.electronAPI?.renderReceiptImagePreview;

  const openExactPreview = async (kind = 'receipt') => {
    setPreviewKind(kind);
    setPreviewOpen(true);
    setPreviewErr('');
    setPreviewLoading(true);
    setPreviewImg('');
    const res = await renderExactPreview(settings, kind, restaurant?._id || '');
    if (res.success && res.dataUrl) {
      setPreviewImg(res.dataUrl);
    } else {
      setPreviewErr(res.error || 'Could not render preview');
    }
    setPreviewLoading(false);
  };

  // Adopt admin-pushed print settings (once per admin update), then reflect them
  // in the form. Runs whenever the admin push timestamp changes.
  useEffect(() => {
    if (applyAdminPrintSettings(restaurant)) {
      const s = localStorage.getItem('printerSettings');
      if (s) { try { setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(s) }); } catch (_) {} }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.printSettings?.updatedAt]);

  // Load settings from localStorage on mount + load printers list
  useEffect(() => {
    const savedSettings = localStorage.getItem('printerSettings');
    if (savedSettings) {
      try {
        setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(savedSettings) });
      } catch (e) {
        console.error('Failed to parse printer settings:', e);
      }
    }
    // Load selected printer
    const sp = localStorage.getItem('selectedPrinter');
    if (sp) setSelectedPrinter(sp);
    // Load system printers list
    if (window.electronAPI?.getPrinters) {
      window.electronAPI.getPrinters().then(printers => {
        setSystemPrinters(printers || []);
      }).catch(() => {});
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('printerSettings', JSON.stringify(newSettings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    saveSettings(newSettings);
  };

  // Test print function
  const handleTestPrint = async () => {
    setTestPrinting(true);

    const _paperPx = settings.paperWidth === '80' ? 576 : 384;
    const _mlPx = parseInt(settings.marginLeft || '2') || 2;
    const _mrPx = parseInt(settings.marginRight || '2') || 2;
    const _cw = parseInt(settings.fontSize || '9') * 0.6;
    const W = Math.max(20, Math.floor((_paperPx - _mlPx - _mrPx) / _cw));
    const line = '='.repeat(W);
    const dash = '-'.repeat(W);
    const pad = (t) => {
      const s = String(t);
      return ' '.repeat(Math.max(0, Math.floor((W - s.length) / 2))) + s;
    };
    const rAlign = (label, val) => { const v = String(val); const l = String(label); const gap = Math.max(1, W - l.length - v.length); return l + ' '.repeat(gap) + v; };
    const is58 = W <= 42;
    const colQty = 3;
    const colPrice = is58 ? 7 : 9;
    const colAmt = is58 ? 8 : 12;
    const nameW = W - colQty - colPrice - colAmt;

    let r = '';
    r += pad('TEST PRINT') + '\n';
    r += pad('Printer Settings Test') + '\n';
    r += line + '\n';
    r += 'Paper: ' + settings.paperWidth + 'mm\n';
    r += 'Font: ' + settings.fontSize + 'px\n';
    r += 'Line Height: ' + settings.lineHeight + '\n';
    r += dash + '\n';
    r += 'Item'.padEnd(nameW) + 'Qty'.padStart(colQty) + 'Price'.padStart(colPrice) + 'Amount'.padStart(colAmt) + '\n';
    r += dash + '\n';
    r += 'Test Item 1'.substring(0, nameW).padEnd(nameW) + String(2).padStart(colQty) + String(100).padStart(colPrice) + String(200).padStart(colAmt) + '\n';
    r += 'Test Item 2'.substring(0, nameW).padEnd(nameW) + String(1).padStart(colQty) + String(150).padStart(colPrice) + String(150).padStart(colAmt) + '\n';
    r += 'Test Item 3'.substring(0, nameW).padEnd(nameW) + String(3).padStart(colQty) + String(50).padStart(colPrice) + String(150).padStart(colAmt) + '\n';
    r += dash + '\n';
    r += rAlign('Total Qty: 6', 'Sub Total 500.00') + '\n';
    r += rAlign('500.00@ 2.5%CGST', '12.50') + '\n';
    r += rAlign('500.00@ 2.5%SGST', '12.50') + '\n';
    r += line + '\n';
    r += '<b>' + rAlign('Grand Total', 'Rs.525') + '</b>\n';
    r += line + '\n';
    r += pad('If you can read this clearly,') + '\n';
    r += pad('your settings are correct!') + '\n';
    r += dash + '\n';
    r += pad('FoodWinc POS') + '\n';

    const printContent = '<!DOCTYPE html><html data-printer="' + selectedPrinter + '" data-paper="' + settings.paperWidth + '" data-printer-type="' + (settings.printerType || 'escpos') + '" data-direction="' + (settings.printDirection || '1') + '" data-density="' + (settings.printDensity || '8') + '" data-speed="' + (settings.printSpeed || '4') + '" data-margin-bottom="' + (settings.marginBottom || '0') + '" data-raster-band="' + (settings.rasterBandHeight || '0') + '"><head><meta charset="utf-8"><title>Test Print</title>' +
      '<style>' +
      '@page{margin:0!important;padding:0!important}' +
      'html{margin:0!important;padding:0!important}' +
      '*{box-sizing:border-box}' +
      'body{margin:0!important;padding:0 ' + (settings.marginRight || '2') + 'px 0 ' + (settings.marginLeft || '2') + 'px;font-family:Courier,monospace;font-size:' + settings.fontSize + 'px;font-weight:' + settings.fontWeight + ';line-height:' + settings.lineHeight + ';width:100%}' +
      'pre{margin:0;padding:0;white-space:pre-wrap;word-wrap:break-word}' +
      '</style></head><body><pre>' + r + '</pre></body></html>';

    // Try Electron silent print first
    if (window.electronAPI?.silentPrint) {
      try {
        const result = await window.electronAPI.silentPrint(printContent);
        if (result.success) {
          console.log('Test print successful:', result.printer);
        } else if (result.stage === 'print') {
          // The bytes already reached the printer — it may have physically printed
          // (e.g. a slow printer that finished after the timeout). Do NOT frame this
          // as a hard failure that invites another click (which would print again).
          console.warn('Test print reported a printer-stage issue (no retry — avoids duplicate):', result.error);
          alert('Test sent to the printer. If nothing came out, check the printer (paper, power, cable) — do not re-tap, it may already be printing.');
        } else {
          console.error('Test print failed:', result.error);
          alert('Print failed: ' + result.error);
        }
      } catch (err) {
        console.error('Test print error:', err);
        alert('Print error: ' + err.message);
      }
    } else {
      // Browser fallback - open print dialog
      const printWindow = window.open('', '_blank', 'width=400,height=600');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
    }

    setTestPrinting(false);
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      margin: 0
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    },
    cardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a2e',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    settingRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f0f0f0'
    },
    settingLabel: {
      fontSize: '14px',
      color: '#333'
    },
    settingDesc: {
      fontSize: '12px',
      color: '#888',
      marginTop: '4px'
    },
    select: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '14px',
      minWidth: '120px',
      cursor: 'pointer'
    },
    input: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '14px',
      width: '80px',
      textAlign: 'center'
    },
    infoBox: {
      background: '#f0f7ff',
      border: '1px solid #cce0ff',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    infoTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#0066cc',
      marginBottom: '8px'
    },
    infoText: {
      fontSize: '13px',
      color: '#444',
      lineHeight: '1.5',
      margin: 0
    },
    infoList: {
      fontSize: '13px',
      color: '#444',
      lineHeight: '1.8',
      margin: '8px 0 0 0',
      paddingLeft: '20px'
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
      color: 'white'
    },
    savedBadge: {
      background: '#10b981',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Printer Settings</h1>
        <p style={styles.subtitle}>Configure your thermal receipt printer</p>
        {saved && <span style={styles.savedBadge}>Saved!</span>}
      </div>

      {adminManaged && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          ℹ️ These format settings were set by <b>FoodWinc admin</b>. You can still adjust them here for this terminal; the printer selection below always stays local to this device.
        </div>
      )}

      {/* Master Print Toggle */}
      <div style={{
        ...styles.card,
        border: settings.printEnabled === 'false' ? '2px solid #e74c3c' : '2px solid #27ae60',
        background: settings.printEnabled === 'false' ? '#fdf2f2' : '#f0faf0',
      }}>
        <div style={styles.settingRow}>
          <div>
            <div style={{ ...styles.settingLabel, fontSize: '16px' }}>
              {settings.printEnabled === 'false' ? '🔴 Printing Disabled' : '🟢 Printing Enabled'}
            </div>
            <div style={styles.settingDesc}>
              {settings.printEnabled === 'false'
                ? 'No receipts will be printed on order accept (auto or manual)'
                : 'Receipts will print automatically when orders are accepted'}
            </div>
          </div>
          <select
            style={{
              ...styles.select,
              backgroundColor: settings.printEnabled === 'false' ? '#e74c3c' : '#27ae60',
              color: '#fff',
              fontWeight: 'bold',
            }}
            value={settings.printEnabled || 'true'}
            onChange={(e) => handleChange('printEnabled', e.target.value)}
          >
            <option value="true">ON</option>
            <option value="false">OFF</option>
          </select>
        </div>
      </div>

      {/* Setup Instructions */}
      <div style={styles.infoBox}>
        <div style={styles.infoTitle}>Printer Setup Instructions</div>
        <p style={styles.infoText}>
          For best results, also configure your printer driver:
        </p>
        <ol style={styles.infoList}>
          <li>Right-click your thermal printer and select <strong>Printing Preferences</strong></li>
          <li>In the Paper/Quality tab, click <strong>Advanced</strong> or <strong>Paper Options</strong></li>
          <li>Select your paper size (e.g., <strong>58mm x Receipt</strong> or <strong>80mm x Receipt</strong>)</li>
          <li>For custom size, enter the exact width and height</li>
          <li>Click <strong>Apply</strong> and <strong>OK</strong></li>
        </ol>
      </div>

      {/* Paper Size Settings */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Paper Size</span>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Paper Width</div>
            <div style={styles.settingDesc}>Common: 58mm (2 inch) or 80mm (3 inch)</div>
          </div>
          <select
            style={styles.select}
            value={settings.paperWidth}
            onChange={(e) => handleChange('paperWidth', e.target.value)}
          >
            <option value="58">58mm (2 inch)</option>
            <option value="80">80mm (3 inch)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Paper Height</div>
            <div style={styles.settingDesc}>Receipt length in mm (150mm = ~6 inches)</div>
          </div>
          <input
            type="number"
            style={styles.input}
            value={settings.paperHeight}
            onChange={(e) => handleChange('paperHeight', e.target.value)}
            min="50"
            max="300"
          />
        </div>
      </div>

      {/* KOT (Kitchen Order Ticket) */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>KOT Slip (Kitchen Order Ticket)</span>
        </div>
        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Print KOT with Receipt</div>
            <div style={styles.settingDesc}>Prints a simplified slip for the kitchen (items + qty, no prices)</div>
          </div>
          <select
            style={styles.select}
            value={settings.kotEnabled || 'true'}
            onChange={(e) => handleChange('kotEnabled', e.target.value)}
          >
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </div>
        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Print ONLY KOT (no customer receipt)</div>
            <div style={styles.settingDesc}>When enabled, orders print the kitchen ticket only — the customer receipt is skipped entirely. Overrides the option above.</div>
          </div>
          <select
            style={styles.select}
            value={settings.kotOnly || 'false'}
            onChange={(e) => handleChange('kotOnly', e.target.value)}
          >
            <option value="false">Disabled</option>
            <option value="true">Enabled</option>
          </select>
        </div>
        <div style={{ ...styles.settingRow, borderBottom: 'none' }}>
          <div>
            <div style={styles.settingDesc}>KOT uses the same font size, margins, and paper settings as the receipt</div>
          </div>
        </div>
      </div>

      {/* Printer Protocol */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Printer Protocol</span>
        </div>
        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Printer Type</div>
            <div style={styles.settingDesc}>ESC/POS: Epson, Star, Citizen, Bixolon, most receipt printers. TSPL: TSC label printers (DA310, etc.)</div>
          </div>
          <select
            style={styles.select}
            value={settings.printerType || 'escpos'}
            onChange={(e) => handleChange('printerType', e.target.value)}
          >
            <option value="escpos">ESC/POS (Most Printers)</option>
            <option value="tspl">TSPL (TSC Printers)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Print Direction</div>
            <div style={styles.settingDesc}>Flip if receipt prints upside down</div>
          </div>
          <select
            style={styles.select}
            value={settings.printDirection || '1'}
            onChange={(e) => handleChange('printDirection', e.target.value)}
          >
            <option value="0">Normal (0°)</option>
            <option value="1">Flipped (180°)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Print Darkness</div>
            <div style={styles.settingDesc}>Higher = darker print (0-15)</div>
          </div>
          <select
            style={styles.select}
            value={settings.printDensity || '8'}
            onChange={(e) => handleChange('printDensity', e.target.value)}
          >
            <option value="4">Light (4)</option>
            <option value="6">Medium-Light (6)</option>
            <option value="8">Normal (8)</option>
            <option value="10">Medium-Dark (10)</option>
            <option value="12">Dark (12)</option>
            <option value="15">Max (15)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Band Raster (fix partial printing)</div>
            <div style={styles.settingDesc}>ESC/POS only. Turn on if the printer prints ~80% then the rest on the NEXT receipt (e.g. OCOM). Off = normal.</div>
          </div>
          <select
            style={styles.select}
            value={settings.rasterBandHeight || '0'}
            onChange={(e) => handleChange('rasterBandHeight', e.target.value)}
          >
            <option value="0">Off (default)</option>
            <option value="128">On — 128 rows</option>
            <option value="256">On — 256 rows (recommended)</option>
            <option value="512">On — 512 rows</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Print Speed</div>
            <div style={styles.settingDesc}>Lower = better quality, higher = faster</div>
          </div>
          <select
            style={styles.select}
            value={settings.printSpeed || '4'}
            onChange={(e) => handleChange('printSpeed', e.target.value)}
          >
            <option value="1">1 (Slowest / Best Quality)</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4 (Default)</option>
            <option value="5">5</option>
            <option value="6">6 (Fastest)</option>
          </select>
        </div>
      </div>

      {/* Font Settings */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Text Settings</span>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Font Size</div>
            <div style={styles.settingDesc}>Smaller = more content fits per line</div>
          </div>
          <select
            style={styles.select}
            value={settings.fontSize}
            onChange={(e) => handleChange('fontSize', e.target.value)}
          >
            <option value="6">6px (Micro)</option>
            <option value="7">7px (Tiny)</option>
            <option value="8">8px (Small)</option>
            <option value="9">9px (Normal)</option>
            <option value="10">10px (Medium)</option>
            <option value="11">11px (Large)</option>
            <option value="12">12px (XL)</option>
            <option value="13">13px (XXL)</option>
            <option value="14">14px (Jumbo)</option>
            <option value="16">16px</option>
            <option value="18">18px</option>
            <option value="20">20px</option>
            <option value="22">22px</option>
            <option value="24">24px</option>
            <option value="28">28px</option>
            <option value="32">32px (Max)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Font Weight</div>
            <div style={styles.settingDesc}>Bold is more readable on thermal paper</div>
          </div>
          <select
            style={styles.select}
            value={settings.fontWeight}
            onChange={(e) => handleChange('fontWeight', e.target.value)}
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Line Spacing</div>
            <div style={styles.settingDesc}>Tighter = more content fits</div>
          </div>
          <select
            style={styles.select}
            value={settings.lineHeight}
            onChange={(e) => handleChange('lineHeight', e.target.value)}
          >
            <option value="0.9">0.9 (Extra Tight)</option>
            <option value="1.0">1.0 (Tight)</option>
            <option value="1.15">1.15 (Compact)</option>
            <option value="1.3">1.3 (Normal)</option>
            <option value="1.5">1.5 (Relaxed)</option>
            <option value="1.8">1.8 (Spacious)</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Left Margin</div>
            <div style={styles.settingDesc}>Space from left edge (px)</div>
          </div>
          <select
            style={styles.select}
            value={settings.marginLeft || '2'}
            onChange={(e) => handleChange('marginLeft', e.target.value)}
          >
            <option value="0">0px (None)</option>
            <option value="1">1px</option>
            <option value="2">2px (Default)</option>
            <option value="3">3px</option>
            <option value="4">4px</option>
            <option value="6">6px</option>
            <option value="8">8px</option>
          </select>
        </div>

        <div style={styles.settingRow}>
          <div>
            <div style={styles.settingLabel}>Right Margin</div>
            <div style={styles.settingDesc}>Space from right edge (px)</div>
          </div>
          <select
            style={styles.select}
            value={settings.marginRight || '2'}
            onChange={(e) => handleChange('marginRight', e.target.value)}
          >
            <option value="0">0px (None)</option>
            <option value="1">1px</option>
            <option value="2">2px (Default)</option>
            <option value="3">3px</option>
            <option value="4">4px</option>
            <option value="6">6px</option>
            <option value="8">8px</option>
          </select>
        </div>

        <div style={{ ...styles.settingRow, borderBottom: 'none' }}>
          <div>
            <div style={styles.settingLabel}>Bottom Margin</div>
            <div style={styles.settingDesc}>Space after content before cut (mm) — type any value, or use Quick set</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="number"
            style={{ ...styles.select, width: '90px' }}
            value={settings.marginBottom || '0'}
            onChange={(e) => handleChange('marginBottom', e.target.value)}
            min="-40" max="300" step="1"
            title="Custom value in mm — type any number"
          />
          <select
            style={styles.select}
            value=""
            onChange={(e) => { if (e.target.value !== '') handleChange('marginBottom', e.target.value); }}
          >
            <option value="">Quick set…</option>
            <option value="50">+50mm (5cm)</option>
            <option value="40">+40mm (4cm)</option>
            <option value="30">+30mm (3cm)</option>
            <option value="20">+20mm (2cm)</option>
            <option value="15">+15mm</option>
            <option value="10">+10mm</option>
            <option value="5">+5mm</option>
            <option value="2">+2mm</option>
            <option value="0">0mm (Default)</option>
            <option value="-5">-5mm (Trim)</option>
            <option value="-10">-10mm</option>
            <option value="-15">-15mm</option>
            <option value="-20">-20mm</option>
            <option value="-25">-25mm</option>
            <option value="-30">-30mm</option>
            <option value="-40">-40mm (Trim Max)</option>
          </select>
          </div>
        </div>
      </div>

      {/* Select Printer — Electron only */}
      {window.electronAPI?.getPrinters && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span>Select Printer</span>
          </div>
          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Printer</div>
              <div style={styles.settingDesc}>
                {systemPrinters.length === 0 ? 'No printers found' : `${systemPrinters.length} printer(s) detected`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                style={{ ...styles.select, minWidth: '200px' }}
                value={selectedPrinter}
                onChange={(e) => {
                  setSelectedPrinter(e.target.value);
                  localStorage.setItem('selectedPrinter', e.target.value);
                  setSaved(true);
                  setTimeout(() => setSaved(false), 2000);
                }}
              >
                <option value="">System Default</option>
                {systemPrinters.map(p => (
                  <option key={p.name} value={p.name}>
                    {p.name}{p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
              <button
                style={{ ...styles.button, padding: '8px 12px', fontSize: '12px', background: '#f0f0f0', color: '#333' }}
                onClick={() => {
                  if (window.electronAPI?.getPrinters) {
                    window.electronAPI.getPrinters().then(printers => setSystemPrinters(printers || []));
                  }
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Print + Preview */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Test Your Settings</span>
        </div>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
          Print a test receipt or preview how it will look.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
            onClick={handleTestPrint}
            disabled={testPrinting}
          >
            {testPrinting ? 'Printing...' : 'Print Test Receipt'}
          </button>
          {supportsExactPreview ? (
            <button
              style={{ ...styles.button, flex: 1, background: '#111827', color: '#fff' }}
              onClick={() => openExactPreview('receipt')}
              title="Shows the exact 1-bit image this terminal sends to the printer"
            >
              👁 Preview (exact)
            </button>
          ) : window.electronAPI?.printPreview ? (
            // Fallback for POS shells older than v1.0.16 (no exact renderer yet):
            // keep the previous on-screen driver preview so nothing is lost.
            <button
              style={{ ...styles.button, flex: 1, background: '#f0f0f0', color: '#333' }}
              onClick={async () => {
                setTestPrinting(true);
                const W = settings.paperWidth === '80' ? 42 : 32;
                const line = '='.repeat(W);
                const pad = (t) => { const s = String(t); return ' '.repeat(Math.max(0, Math.floor((W - s.length) / 2))) + s; };
                const r = pad('PREVIEW') + '\n' + line + '\n' + pad('This is how your receipt') + '\n' + pad('will look when printed.') + '\n' + line + '\n' + pad('FoodWinc POS') + '\n';
                const html = '<!DOCTYPE html><html data-paper="' + settings.paperWidth + '"><head><meta charset="utf-8"><style>' +
                  '@page{margin:0!important;padding:0!important}html{margin:0!important;padding:0!important}*{box-sizing:border-box;-webkit-print-color-adjust:exact!important}' +
                  'body{margin:0!important;padding:0 0 0 2px;font-family:"Courier New",Courier,monospace;font-size:' + settings.fontSize + 'px;font-weight:' + settings.fontWeight + ';line-height:' + settings.lineHeight + ';width:100%}' +
                  'pre{margin:0;padding:0;white-space:pre-wrap;word-wrap:break-word}' +
                  '</style></head><body><pre>' + r + '</pre></body></html>';
                try { await window.electronAPI.printPreview(html); } catch (_) {}
                setTestPrinting(false);
              }}
            >
              Preview
            </button>
          ) : null}
        </div>
        {supportsExactPreview && (
          <p style={{ fontSize: '12px', color: '#888', margin: '10px 0 0' }}>
            The preview shows the exact black/white raster this terminal sends to the printer with the current settings.
          </p>
        )}
      </div>

      {/* Quick Presets */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span>Quick Presets</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.button, background: '#f0f0f0', color: '#333', flex: 1 }}
            onClick={() => saveSettings({ ...DEFAULT_SETTINGS, paperWidth: '58', paperHeight: '150', fontSize: '9' })}
          >
            58mm Standard
          </button>
          <button
            style={{ ...styles.button, background: '#f0f0f0', color: '#333', flex: 1 }}
            onClick={() => saveSettings({ ...DEFAULT_SETTINGS, paperWidth: '80', paperHeight: '200', fontSize: '10' })}
          >
            80mm Standard
          </button>
          <button
            style={{ ...styles.button, background: '#f0f0f0', color: '#333', flex: 1 }}
            onClick={() => saveSettings({ ...DEFAULT_SETTINGS, paperWidth: '58', paperHeight: '100', fontSize: '8', lineHeight: '1.0' })}
          >
            58mm Compact
          </button>
        </div>
      </div>

      {/* Accurate preview modal — shows the exact raster the printer receives */}
      {previewOpen && (
        <div
          onClick={() => setPreviewOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, maxHeight: '90vh', overflow: 'auto', width: 'min(440px, 92vw)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: '#111827' }}>Exact print preview</div>
              <button onClick={() => setPreviewOpen(false)} style={{ border: 'none', background: '#f0f0f0', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={() => openExactPreview('receipt')} style={{ ...styles.button, flex: 1, padding: '8px 12px', fontSize: 13, background: previewKind === 'receipt' ? '#111827' : '#f0f0f0', color: previewKind === 'receipt' ? '#fff' : '#333' }}>Receipt</button>
              <button onClick={() => openExactPreview('kot')} style={{ ...styles.button, flex: 1, padding: '8px 12px', fontSize: 13, background: previewKind === 'kot' ? '#111827' : '#f0f0f0', color: previewKind === 'kot' ? '#fff' : '#333' }}>KOT</button>
            </div>
            <p style={{ fontSize: 12, color: '#888', margin: '0 0 12px' }}>
              This is the exact black/white image sent to the printer ({settings.paperWidth}mm). Sample content; your real receipts use the same layout.
            </p>
            <div style={{ background: '#e5e7eb', padding: 16, borderRadius: 8, display: 'flex', justifyContent: 'center', minHeight: 120, alignItems: 'center' }}>
              {previewLoading ? (
                <span style={{ color: '#6b7280' }}>Rendering…</span>
              ) : previewErr ? (
                <span style={{ color: '#dc2626', fontSize: 13, textAlign: 'center' }}>{previewErr}</span>
              ) : previewImg ? (
                <img
                  src={previewImg}
                  alt="Receipt preview"
                  style={{ width: settings.paperWidth === '80' ? 320 : 240, imageRendering: 'pixelated', background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrinterSettings;
